import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import useCountdown from "./components/useCountdown";

const GREEN = "#03B349";
const GREEN_DEEP = "#0a7a34";
const GREEN_BRIGHT = "#00ff41";

const unwrap = (res) => (res && res.status === "success" ? res.data : null);

/**
 * Compact, in-flow promo strip for the active lottery. Mounted globally in the
 * lobby layout so it shows on every page except the lottery page itself, with a
 * live countdown to the draw. Re-fetches on every navigation change so it stays
 * fresh without a manual refresh; clicking anywhere navigates to /lottery.
 */
const LotteryBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [lottery, setLottery] = useState(null);

  // Re-run on each route change so a newly-started (or ended) lottery is picked
  // up as the player moves around, without needing a page refresh.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getAllData(endPoints.api.GET_ACTIVE_LOTTERY);
        const data = unwrap(res);
        if (alive) setLottery(data?.id != null ? data : null);
      } catch (e) {
        if (alive) setLottery(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [location.pathname]);

  const countdown = useCountdown(lottery?.drawTime);

  // Hide on the lottery page itself, and when there is nothing live to promote.
  if (location.pathname.startsWith("/lottery")) return null;
  if (!lottery?.id || countdown?.ended) return null;

  // Only ever show the server-formatted amount — it is converted into the
  // player's own currency. When it is missing, promote the draw without a
  // number rather than showing a raw SC value nobody recognises.
  const prizeLabel = lottery?.prizeDisplay || null;
  const pad = (n) => String(n ?? 0).padStart(2, "0");
  const timeStr =
    (countdown?.days > 0 ? `${pad(countdown.days)}d ` : "") +
    `${pad(countdown?.hours)}h ${pad(countdown?.minutes)}m ${pad(countdown?.seconds)}s`;

  return (
    <Box
      onClick={() => navigate("/lottery")}
      sx={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.25, md: 2 },
        background: "linear-gradient(135deg, #0d2b16 0%, #123c1f 45%, #191919 100%)",
        border: `1px solid ${GREEN}55`,
        borderLeft: `4px solid ${GREEN}`,
        borderRadius: 2,
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1.1, md: 1.5 },
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(160deg, ${GREEN} 0%, ${GREEN_DEEP} 100%)`,
          borderRadius: "50%",
          width: { xs: 34, md: 44 },
          height: { xs: 34, md: 44 },
          flexShrink: 0,
        }}
      >
        <ConfirmationNumberIcon sx={{ fontSize: { xs: 18, md: 24 }, color: "#04240f" }} />
      </Box>

      <Stack sx={{ minWidth: 0, flex: 1, gap: 0.6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              background: "rgba(0,255,65,0.14)",
              border: `1px solid ${GREEN}66`,
              color: GREEN_BRIGHT,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.6,
              borderRadius: "6px",
              px: 0.8,
              py: 0.25,
              maxWidth: { xs: 110, md: 180 },
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <ConfirmationNumberIcon sx={{ fontSize: 13, flexShrink: 0 }} />
            {lottery?.name || "Lottery"}
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              background: "rgba(0,0,0,0.3)",
              borderRadius: "6px",
              px: 0.9,
              py: 0.25,
              flexShrink: 0,
            }}
          >
            <HourglassBottomRoundedIcon sx={{ fontSize: 13, color: GREEN_BRIGHT }} />
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: GREEN_BRIGHT,
                letterSpacing: 0.5,
                // Without this the countdown breaks onto four lines on mobile
                // and stretches the whole banner.
                whiteSpace: "nowrap",
              }}
            >
              {timeStr}
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            fontWeight: 800,
            color: "#fff",
            fontSize: { xs: 12.5, md: 15 },
            lineHeight: 1.3,
            // Keep the banner a predictable height on narrow screens.
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          Hurry — win{" "}
          <Box component="span" sx={{ color: GREEN_BRIGHT }}>
            {prizeLabel || "an amazing reward"}
          </Box>{" "}
          before time runs out!
        </Typography>
      </Stack>

      <Button
        onClick={(e) => {
          e.stopPropagation();
          navigate("/lottery");
        }}
        endIcon={<ArrowForwardRoundedIcon />}
        sx={{
          // The whole banner is clickable, so the button is redundant on mobile
          // where it costs the headline its width.
          display: { xs: "none", sm: "inline-flex" },
          flexShrink: 0,
          background: `linear-gradient(135deg, ${GREEN}, ${GREEN_BRIGHT})`,
          color: "#04240f",
          fontWeight: 800,
          fontSize: { xs: 12, md: 13 },
          borderRadius: "22px",
          px: { xs: 1.6, md: 2.2 },
          py: 0.7,
          textTransform: "none",
          whiteSpace: "nowrap",
          "&:hover": { background: `linear-gradient(135deg, ${GREEN_BRIGHT}, ${GREEN})` },
        }}
      >
        Play now
      </Button>
    </Box>
  );
};

export default LotteryBanner;
